'use client';

import React, { useState, useMemo, useCallback } from 'react';
import Level2Layout from '@/components/templates/Level2Layout';
import { NewsFilters } from '@/components/molecules/NewsFilters';
import type { NewsFilters as NewsFiltersType, FilterOption } from '@/types/filters';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface ContentItem {
  id: string;
  title: string;
  description: string;
  href: string;
  date: string;
  tags?: string[];
  status?: 'active' | 'coming-soon' | 'new';
  category?: string;
  subCategory?: string;
}

interface FeatureItem {
  title: string;
  description: string;
  icon?: string;
}

interface CategoryPageClientProps {
  breadcrumbs: BreadcrumbItem[];
  title: string;
  description: string;
  items: ContentItem[];
  features?: {
    title: string;
    items: FeatureItem[];
  };
  ctaSection?: {
    title: string;
    description: string;
    buttonText: string;
    buttonHref: string;
    icon?: string;
  };
  emptyMessage?: string;
  enableFilters?: boolean;
  showSubCategoryFilter?: boolean;
  showDateFilter?: boolean;
  showTagFilter?: boolean;
}

export const CategoryPageClient: React.FC<CategoryPageClientProps> = ({
  breadcrumbs,
  title,
  description,
  items: initialItems,
  features,
  ctaSection,
  emptyMessage,
  enableFilters = true,
  showSubCategoryFilter = true,
  showDateFilter = true,
  showTagFilter = true,
}) => {
  const [filters, setFilters] = useState<NewsFiltersType>({});

  // Extract unique subcategories from items
  const subCategories = useMemo<FilterOption[]>(() => {
    const categoryMap = new Map<string, number>();
    
    initialItems.forEach(item => {
      if (item.subCategory) {
        categoryMap.set(item.subCategory, (categoryMap.get(item.subCategory) || 0) + 1);
      }
    });

    const categories = Array.from(categoryMap.entries()).map(([value, count]) => ({
      value,
      label: getSubCategoryLabel(value),
      count
    }));

    return categories.sort((a, b) => a.label.localeCompare(b.label, 'ja'));
  }, [initialItems]);

  // Extract unique tags from items
  const availableTags = useMemo<FilterOption[]>(() => {
    const tagMap = new Map<string, number>();
    
    initialItems.forEach(item => {
      item.tags?.forEach(tag => {
        tagMap.set(tag, (tagMap.get(tag) || 0) + 1);
      });
    });

    const tags = Array.from(tagMap.entries()).map(([value, count]) => ({
      value,
      label: value,
      count
    }));

    return tags.sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [initialItems]);

  // Filter items based on current filters
  const filteredItems = useMemo(() => {
    let filtered = [...initialItems];

    // Filter by subcategory
    if (filters.subCategory) {
      filtered = filtered.filter(item => item.subCategory === filters.subCategory);
    }

    // Filter by date range
    if (filters.startDate || filters.endDate) {
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        const startDate = filters.startDate ? new Date(filters.startDate) : null;
        const endDate = filters.endDate ? new Date(filters.endDate) : null;

        if (startDate && itemDate < startDate) {
          return false;
        }
        if (endDate && itemDate > endDate) {
          return false;
        }
        
        return true;
      });
    }

    // Filter by tags
    if (filters.tags && filters.tags.length > 0) {
      filtered = filtered.filter(item => 
        item.tags && filters.tags!.some(tag => item.tags!.includes(tag))
      );
    }

    return filtered;
  }, [initialItems, filters]);

  const handleFiltersChange = useCallback((newFilters: NewsFiltersType) => {
    setFilters(newFilters);
  }, []);

  return (
    <>
      {enableFilters && (subCategories.length > 0 || availableTags.length > 0) && (
        <div style={{ padding: '0 1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
          <NewsFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            subCategories={showSubCategoryFilter ? subCategories : []}
            availableTags={showTagFilter ? availableTags : []}
            showSubCategoryFilter={showSubCategoryFilter && subCategories.length > 0}
            showDateFilter={showDateFilter}
            showTagFilter={showTagFilter && availableTags.length > 0}
          />
        </div>
      )}
      
      <Level2Layout
        breadcrumbs={breadcrumbs}
        title={title}
        description={description}
        items={filteredItems}
        features={features}
        ctaSection={ctaSection}
        emptyMessage={
          filteredItems.length === 0 && initialItems.length > 0
            ? 'フィルター条件に一致する項目がありません。'
            : emptyMessage
        }
      />
    </>
  );
};

// Helper function to get Japanese labels for subcategories
function getSubCategoryLabel(subCategory: string): string {
  const labels: Record<string, string> = {
    'press-releases': 'プレスリリース',
    'announcements': 'お知らせ',
    'events': 'イベント・セミナー',
    'service-updates': 'サービス更新',
    'system-development': 'システム開発',
    'content-production': 'コンテンツ制作',
    'consulting': 'コンサルティング',
    'own-projects': '自社プロジェクト',
    'collaborations': 'コラボレーション',
  };
  
  return labels[subCategory] || subCategory;
}